import "./styles/global.scss";
import "./styles/main.scss";
import "./styles/renderer.scss";
import "@radix-ui/themes/styles.css";

import { useCallback, useEffect, useState } from "react";

import Split from "react-split";
import PromptBox from "./components/prompBox";

import InfoContainer from "./components/InfoContainer";
import useStore from "./hooks/useStore";

import { useRef } from "react";

import { ws, DataSource } from "./global";

import { CustomDragLayer } from "./components/customDragLayer";

import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";
import PromptHistoryView from "./components/PromptHistory";
import { UiRenderer } from "./components/renderers/UiRenderer";

import { fakeData } from "./components/renderers/_fakeData/relational-dinner";
import DatabaseRenderer from "./components/dataSchema/DatabaseRenderer";
import { JSON_NAMES } from "./names";

const filenames = JSON_NAMES.names;

function App() {
  const [uiMode, setUiMode] = useState(false);

  const [preloadData, setPreloadData] = useState(null);
  const [left, setLeft] = useState(25);

  const sourceRef = useRef(null);
  const setPrompt = useStore((state) => state.setPrompt);
  const curPrompt = useStore((state) => state._prompt);

  const xray = useStore((state) => state.xray);

  const setData = useStore((state) => state.setData);
  const updateData = useStore((state) => state.updateData);
  const updateStructureSchema = useStore(
    (state) => state.updateStructureSchema
  );
  const setLoadingLegend = useStore((state) => state.setLoadingLegend);
  const setPivotObject = useStore((state) => state.setPivotObject);
  const updateInfoObject = useStore((state) => state.updateInfoObject);
  const updateUnlabeledInfoObject = useStore(
    (state) => state.updateUnlabeledInfoObject
  );
  const setInitialized = useStore((state) => state.setInitialized);
  const initialized = useStore((state) => state.isInitialized);
  const splitRef = useRef(null);

  const addView = useStore((state) => state.addView);
  const setViews = useStore((state) => state.setViews);
  const addPromptHistory = useStore((state) => state.addPromptHistory);
  const setInfoObjects = useStore((state) => state.setInfoObjects);
  const setUnlabeledInfoObjects = useStore(
    (state) => state.setUnlabeledInfoObjects
  );

  const [taskAnalyzing, setTaskAnalyzing] = useState(false);

  const [dataSourceKey, setDataSourceKey] = useState(0);
  const [dataSource, setDataSource] = useState(`/savedJson/${filenames[0]}`);

  const changeDataSource = (e) => {
    const key = parseInt(e.target.value);
    setDataSourceKey(key);
    setDataSource(`/savedJson/${filenames[key]}`);
  };

  const handleNext = () => {
    const nextKey = (dataSourceKey + 1) % filenames.length;
    setDataSourceKey(nextKey);
    setDataSource(`/savedJson/${filenames[nextKey]}`);
  };

  const handlePrevious = () => {
    const prevKey = (dataSourceKey - 1 + filenames.length) % filenames.length;
    setDataSourceKey(prevKey);
    setDataSource(`/savedJson/${filenames[prevKey]}`);
  };

  useEffect(() => {
    if (!xray) {
      setLeft(25);
    } else {
      setLeft(30);
    }
  }, [xray]);

  const initViews = useCallback(
    (infoObject, viewType) => {
      addView({ infoObject, path: "", viewType });
    },
    [addView]
  );

  useEffect(() => {
    if (ws)
      ws.onmessage = (e) => {
        const message = JSON.parse(e.data);

        if (message.messageType === "getInitialSchemaAndData") {
          console.log("get initial schema and data");

          setTaskAnalyzing(false);

          let { data, dataSchema, structureSchema, labeledSchema } = message;

          data = JSON.parse(data);
          dataSchema = JSON.parse(dataSchema);
          structureSchema = JSON.parse(structureSchema);
          labeledSchema = JSON.parse(labeledSchema);

          if ("task" in dataSchema) {
            data = data.task;
            dataSchema = dataSchema.task;
            structureSchema = structureSchema.task;
          }

          const rootInfoObjName = Object.keys(dataSchema)[0]; // name of the task schema
          setPivotObject(rootInfoObjName);

          Object.keys(labeledSchema).forEach((key) => {
            updateInfoObject(key, labeledSchema[key]);
            updateUnlabeledInfoObject(key, dataSchema[key]);
          });

          setData(data);

          // TODO case type
          structureSchema[rootInfoObjName] =
            structureSchema[rootInfoObjName] ?? "panel";
          updateStructureSchema(rootInfoObjName, structureSchema);

          const rootSchemaViewType = structureSchema[rootInfoObjName];
          initViews(rootInfoObjName, rootSchemaViewType);
          setInitialized(true);
        } else if (message.messageType === "getDataSchema") {
          setLoadingLegend("generating structure schema ...");
        } else if (message.messageType === "getStructureSchema") {
          setLoadingLegend("getting data ...");
        } else if (message.messageType === "getMockData") {
          setTaskAnalyzing(false);
        } else if (message.messageType === "handleFollowupPrompt") {
          console.log(
            `[${message.opid}] ${JSON.stringify(message.spec, null, 2)}`
          );

          addPromptHistory(message.exp, "op_agent", { opid: message.opid });
        } else if (message.messageType === "updateTaskStructure") {
          const res = message.res;
          console.log("========= updateTaskStructure ========");
          console.log(res);

          addPromptHistory(res.exp, "op_agent", { opid: "[T]" });
          const ts = res.taskStructure;

          updateInfoObject(Object.keys(ts)[0], ts[Object.keys(ts)[0]]);
          if (typeof res.entities_to_add !== "object") {
            res.entities_to_add = JSON.parse(res.entities_to_add);
          }
          if (!Array.isArray(res.entities_to_add)) {
            res.entities_to_add = [res.entities_to_add];
          }
          res.entities_to_add.forEach((e) => {
            try {
              updateInfoObject(Object.keys(e)[0], e[Object.keys(e)[0]]);
            } catch (e) {
              console.log(e);
            }
          });
        } else if (message.messageType === "updateData") {
          console.log(message.data);
          setData(message.data);
        } else if (message.messageType === "updateEntityStructure") {
          console.log(message.entityStructure);
          updateInfoObject(message.entity, message.entityStructure);
        }
      };
  }, [
    addPromptHistory,
    initViews,
    setData,
    setInfoObjects,
    setInitialized,
    setLoadingLegend,
    setPivotObject,
    updateData,
    updateInfoObject,
    updateStructureSchema,
    updateUnlabeledInfoObject,
  ]);

  useEffect(() => {
    setLoadingLegend("generate data schema ...");
  }, [setLoadingLegend, taskAnalyzing]);

  // * if preload data
  useEffect(() => {
    fetch(dataSource)
      .then((res) => res.json())
      .then((data) => setPreloadData(data))
      .catch((error) => console.error("Error fetching JSON:", error));

    // setPreloadData(fakeData);
  }, [dataSource]);

  useEffect(() => {
    if (preloadData) {
      const { data, labeledSchema, dataSchema, __prompt } = preloadData;

      const prompt = preloadData.__prompt;
      setPrompt(prompt);

      const goals = preloadData.goalNTasks;

      const rootInfoObjName = Object.keys(labeledSchema)[0]; // name of the task schema

      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(
          JSON.stringify({
            funcname: "setPreloadTask",
            params: {
              prompt: prompt,
              goals: goals,
            },
          })
        );
      } else {
        console.warn("WebSocket is not ready. Message not sent.");
      }

      setPivotObject(rootInfoObjName);

      setInfoObjects(labeledSchema);
      setUnlabeledInfoObjects(dataSchema);

      setPrompt(__prompt);

      // Object.keys(labeledSchema).forEach((key) => {
      //   updateInfoObject(key, labeledSchema[key]);
      //   updateUnlabeledInfoObject(key, dataSchema[key]);
      // });
      setData(data);

      // TODO case type
      // structureSchema[rootInfoObjName] = "pane"
      //   structureSchema[rootInfoObjName] ?? "panel";
      updateStructureSchema(rootInfoObjName, {
        [rootInfoObjName]: "panel",
      });

      // initializeViews(info_obj, ds, structureSchema.structure);
      const rootSchemaViewType = "panel";
      initViews(rootInfoObjName, rootSchemaViewType);

      setInitialized(true);
    }

    return () => {
      setViews([]);
    };
  }, [
    initViews,
    preloadData,
    setData,
    setInfoObjects,
    setInitialized,
    setPivotObject,
    setPrompt,
    setUnlabeledInfoObjects,
    setViews,
    updateInfoObject,
    updateStructureSchema,
    updateUnlabeledInfoObject,
  ]);

  return (
    <DndProvider backend={HTML5Backend}>
      <CustomDragLayer />

      <div className="outer-container">
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
            zIndex: 999,
            width: "100%",
            padding: "4px 32px 0px 32px",
            backgroundColor: "#f7f7f7",
          }}
        >
          <div style={{ fontSize: "20px", fontWeight: "bold" }}>
            {curPrompt}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <select
              value={dataSourceKey}
              onChange={changeDataSource}
              style={{
                height: "fit-content",
              }}
            >
              {filenames.map((filename, key) => (
                <option key={key} value={key}>
                  {filename}
                </option>
              ))}
            </select>
            <button
              style={{ fontSize: "16px", cursor: "pointer" }}
              onClick={handlePrevious}
            >
              Previous
            </button>
            <button
              style={{ fontSize: "16px", cursor: "pointer" }}
              onClick={handleNext}
            >
              Next
            </button>
          </div>
        </div>

        {initialized &&
          (uiMode ? (
            <div className="outer-container" data-state={xray ? "x-ray" : "ui"}>
              <UiRenderer />
            </div>
          ) : (
            <>
              <Split
                className="outer-container"
                sizes={[left, 100 - left]}
                minSize={xray ? 350 : window.innerWidth * 0.25}
                data-state={xray ? "x-ray" : "ui"}
                ref={splitRef}
              >
                <InfoContainer />
                <DatabaseRenderer />
              </Split>
            </>
          ))}
      </div>
    </DndProvider>
  );
}

export default App;
